import axios from 'axios';

// Get API base URL with environment detection
const getApiBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }


  if (process.env.NODE_ENV === 'development') {
    return '/api';
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }

  return 'http://localhost:4000/api';
};

// Get base URL for assets and images
const getBaseURL = () => {

  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }


  return 'http://localhost:4000';
};

export const API_BASE_URL = getApiBaseURL();
export const BASE_URL = getBaseURL();


console.log('🌐 API Base URL:', API_BASE_URL);


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});


// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('📤 API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('📥 API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ Response Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      code: error.code
    });
    return Promise.reject(error);
  }
);

// API service functions
export const databaseAPI = {
  // Test database connection
  testConnection: () => api.get('/test-connection'),

  // Get all tables
  getTables: (token) => api.get('/tables', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get table structure
  getTableStructure: (tableName, token) => api.get(`/table/${tableName}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get table data with pagination
  getTableData: (tableName, page = 1, limit = 10, token) =>
    api.get(`/table/${tableName}/data?page=${page}&limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` }
    }),

  // Execute custom query
  executeQuery: (query, token) => api.post('/query', { query }, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const authAPI = {
  // Login
  login: (username, password) => api.post('/auth/login', { username, password }),

  // Verify token
  verifyToken: (token) => api.get('/auth/verify', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Logout
  logout: () => api.post('/auth/logout'),
};

export const vendorAPI = {
  // Get all vendors
  getVendors: (token) => api.get('/vendors', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create new vendor
  createVendor: (vendorData, token) => api.post('/vendors', vendorData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update vendor
  updateVendor: (id, vendorData, token) => api.put(`/vendors/${id}`, vendorData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete vendor
  deleteVendor: (id, token) => api.delete(`/vendors/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Change vendor password (Admin only)
  changePassword: (id, newPassword, token, oldPassword) => api.put(`/admin/vendors/${id}/password`, {
    new_password: newPassword,
    old_password: oldPassword
  }, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Unified image upload for all vendor images
  uploadImage: (imageData, token) => api.post('/upload-image', imageData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Bank Account Management
  getBankAccounts: (token) => api.get('/vendor/bank-accounts', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  addBankAccount: (accountData, token) => api.post('/vendor/bank-accounts', accountData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  updateBankAccount: (id, accountData, token) => api.put(`/vendor/bank-accounts/${id}`, accountData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  deleteBankAccount: (id, token) => api.delete(`/vendor/bank-accounts/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  setDefaultBankAccount: (id, token) => api.put(`/vendor/bank-accounts/${id}/default`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update vendor profile password (Self)
  updateProfilePassword: (passwords, token) => api.put('/vendor/profile/password', passwords, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const productAPI = {
  // Get products with filters
  getProducts: (params = {}, token) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/products?${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Get single product by ID
  getProduct: (id, token) => api.get(`/products/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create new product
  createProduct: (productData, token) => api.post('/products', productData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update product
  updateProduct: (id, productData, token) => api.put(`/products/${id}`, productData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete product
  deleteProduct: (id, token) => api.delete(`/products/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};


export const bannerAPI = {
  // Get all banners
  getBanners: (token) => api.get('/banners', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create new banner
  createBanner: (bannerData, token) => api.post('/banners', bannerData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update banner
  updateBanner: (id, bannerData, token) => api.put(`/banners/${id}`, bannerData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete banner
  deleteBanner: (id, token) => api.delete(`/banners/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const featuredAPI = {
  // Get all featured items
  getFeatured: (token) => {
    console.log('Making API call to /featured with token:', token ? 'Token present' : 'No token');
    return api.get('/featured', {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Create new featured item
  createFeatured: (featuredData, token) => api.post('/featured', featuredData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update featured item
  updateFeatured: (id, featuredData, token) => api.put(`/featured/${id}`, featuredData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete featured item
  deleteFeatured: (id, token) => api.delete(`/featured/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const sectionAPI = {
  getSections: (token) => api.get('/sections', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  getPublicSections: () => api.get('/public/sections'),

  createSection: (sectionData, token) => api.post('/sections', sectionData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  updateSection: (id, sectionData, token) => api.put(`/sections/${id}`, sectionData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  deleteSection: (id, token) => api.delete(`/sections/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  addSectionItem: (sectionId, itemData, token) => api.post(`/sections/${sectionId}/items`, itemData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  updateSectionItem: (itemId, itemData, token) => api.put(`/sections/items/${itemId}`, itemData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  deleteSectionItem: (itemId, token) => api.delete(`/sections/items/${itemId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const categoryAPI = {
  // Get category hierarchy
  getHierarchy: (token) => api.get('/categories/hierarchy', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get categories by level
  getByLevel: (level, parentId, token) => {
    const url = parentId ? `/categories/level/${level}?parent_id=${parentId}` : `/categories/level/${level}`;
    return api.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Create category
  createCategory: (level, categoryData, token) => api.post(`/categories/level/${level}`, categoryData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update category
  updateCategory: (level, id, categoryData, token) => api.put(`/categories/level/${level}/${id}`, categoryData, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete category
  deleteCategory: (level, id, token) => api.delete(`/categories/level/${level}/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const brandAPI = {
  getBrands: (token) => api.get('/brands', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  createBrand: (data, token) => api.post('/brands', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateBrand: (id, data, token) => api.put(`/brands/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  deleteBrand: (id, token) => api.delete(`/brands/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const attributeAPI = {
  // Get all attributes
  getAttributes: (token) => api.get('/attributes', {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get attribute by ID
  getAttribute: (id, token) => api.get(`/attributes/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create attribute
  createAttribute: (data, token) => api.post('/attributes', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update attribute
  updateAttribute: (id, data, token) => api.put(`/attributes/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete attribute
  deleteAttribute: (id, token) => api.delete(`/attributes/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get attribute values
  getAttributeValues: (attributeId, token) => api.get(`/attributes/${attributeId}/values`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create attribute value
  createAttributeValue: (attributeId, value, token) => api.post(`/attributes/${attributeId}/values`, { value }, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete attribute value
  deleteAttributeValue: (id, token) => api.delete(`/attribute-values/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const variationAPI = {
  // Get product variations
  getProductVariations: (productId, token) => api.get(`/products/${productId}/variations`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Get product with variations
  getProductWithVariations: (productId, token) => api.get(`/products/${productId}/with-variations`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create variation
  createVariation: (productId, data, token) => api.post(`/products/${productId}/variations`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update variation
  updateVariation: (id, data, token) => api.put(`/variations/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete variation
  deleteVariation: (id, token) => api.delete(`/variations/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const hsnAPI = {
  // Get all HSN codes
  getHSNCodes: (params = {}, token) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/hsn-codes${queryParams ? '?' + queryParams : ''}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Get single HSN code
  getHSNCode: (id, token) => api.get(`/hsn-codes/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Create HSN code
  createHSNCode: (data, token) => api.post('/hsn-codes', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update HSN code
  updateHSNCode: (id, data, token) => api.put(`/hsn-codes/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete HSN code
  deleteHSNCode: (id, token) => api.delete(`/hsn-codes/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

export const gstAPI = {
  // Get all GST rates
  getGSTRates: (params = {}, token) => {
    const queryParams = new URLSearchParams(params).toString();
    return api.get(`/gst-rates${queryParams ? '?' + queryParams : ''}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Create GST rate
  createGSTRate: (data, token) => api.post('/gst-rates', data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Update GST rate
  updateGSTRate: (id, data, token) => api.put(`/gst-rates/${id}`, data, {
    headers: { Authorization: `Bearer ${token}` }
  }),

  // Delete GST rate
  deleteGSTRate: (id, token) => api.delete(`/gst-rates/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// Unified image upload function - available to all components
export const uploadImage = (imageData, token) => api.post('/upload-image', imageData, {
  headers: { Authorization: `Bearer ${token}` }
});

export default api;
